import type { Identifier } from "ra-core";

import type { AlertRule, DealActivityType, DealStageConfig } from "../types";

/**
 * Almikia Epic ER (TASK-006): pure, framework-free helpers backing the
 * "Alertas" admin screen -- threshold validation, error mapping, plain-Spanish
 * descriptions/summaries and the target-stage/appointment-activity choice
 * lists. Kept free of react-admin/react coupling so they are unit-testable on
 * their own (see `alertRuleForm.test.ts`), mirroring the shape of
 * `stageConfigs/stageConfigForm.ts` / `activityCatalogue/activityTypeForm.ts`.
 */

export const HOURS_THRESHOLD_MIN = 1;
export const HOURS_THRESHOLD_MAX = 2160;
export const STUCK_LEAD_COUNT_MIN = 1;
export const STUCK_LEAD_COUNT_MAX = 100;

function toNumber(value: number | string | null | undefined): number | null {
  if (value == null || value === "") return null;
  const num = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(num) ? num : null;
}

/**
 * `alert_rules.hours_threshold` (`sin_seguimiento` / `alta_intencion_sin_actividad`):
 * a required integer, 1-2160 (matching `enforce_alert_rules_guard()` / the
 * FakeRest guard's own range).
 */
export function validateHoursThreshold(
  value: number | string | null | undefined,
): string | undefined {
  const num = toNumber(value);
  if (num == null) {
    return "resources.alert_rules.error.hours_threshold_required";
  }
  if (
    !Number.isInteger(num) ||
    num < HOURS_THRESHOLD_MIN ||
    num > HOURS_THRESHOLD_MAX
  ) {
    return "resources.alert_rules.error.hours_threshold_invalid";
  }
  return undefined;
}

/**
 * `alert_rules.stuck_lead_count_threshold` (`detenido_en_etapa`): a required
 * integer, 1-100.
 */
export function validateStuckLeadCountThreshold(
  value: number | string | null | undefined,
): string | undefined {
  const num = toNumber(value);
  if (num == null) {
    return "resources.alert_rules.error.stuck_lead_count_required";
  }
  if (
    !Number.isInteger(num) ||
    num < STUCK_LEAD_COUNT_MIN ||
    num > STUCK_LEAD_COUNT_MAX
  ) {
    return "resources.alert_rules.error.stuck_lead_count_invalid";
  }
  return undefined;
}

/** `alert_rules.target_stage_key` (`cumple_criterio_sin_cita`): required. */
export function validateTargetStageKey(
  value: string | null | undefined,
): string | undefined {
  return value
    ? undefined
    : "resources.alert_rules.error.target_stage_required";
}

/**
 * `alert_rules.appointment_activity_type_id` (`cumple_criterio_sin_cita`):
 * required.
 */
export function validateAppointmentActivityTypeId(
  value: Identifier | null | undefined,
): string | undefined {
  return value != null
    ? undefined
    : "resources.alert_rules.error.appointment_activity_required";
}

/**
 * Maps a save/write error to a Spanish message, so a raw Postgres/RLS error
 * text is never shown to direccion -- mirrors `toStageConfigErrorMessage`. A
 * FakeRest guard error is already a plain Spanish message (see
 * `alertsProvider.ts`), so it passes through UNCHANGED; only an RLS refusal
 * (code `42501`, or "row-level security" in the text) is remapped, and a
 * message-less error falls back to the generic message.
 */
export function toAlertRuleErrorMessage(error: unknown): string {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String((error as { code?: unknown }).code)
      : undefined;
  const message = error instanceof Error ? error.message : undefined;

  if (code === "42501" || /row-level security/i.test(message ?? "")) {
    return "resources.alert_rules.error.not_authorized";
  }
  return message || "resources.alert_rules.error.generic";
}

export interface AlertRuleTranslation {
  key: string;
  params: Record<string, unknown>;
}

type DescribableAlertRule = Pick<
  AlertRule,
  | "rule_type"
  | "hours_threshold"
  | "stuck_lead_count_threshold"
  | "target_stage_key"
  | "appointment_activity_type_id"
>;

/**
 * Acceptance criterion (TASK-006): "a plain-Spanish description of when the
 * alert appears" -- one sentence per rule type, filled in with the rule's
 * OWN configured parameters (never a generic template), second person,
 * matching the Independence-guide vocabulary.
 */
export function resolveAlertRuleDescription(
  rule: DescribableAlertRule,
  stageLabelByKey: ReadonlyMap<string, string>,
  activityNameById: ReadonlyMap<Identifier, string>,
): AlertRuleTranslation {
  switch (rule.rule_type) {
    case "sin_seguimiento":
      return {
        key: "resources.alert_rules.description.sin_seguimiento",
        params: { hours: rule.hours_threshold ?? 0 },
      };
    case "detenido_en_etapa":
      return {
        key: "resources.alert_rules.description.detenido_en_etapa",
        params: { count: rule.stuck_lead_count_threshold ?? 0 },
      };
    case "cumple_criterio_sin_cita":
      return {
        key: "resources.alert_rules.description.cumple_criterio_sin_cita",
        params: {
          stage:
            rule.target_stage_key != null
              ? (stageLabelByKey.get(rule.target_stage_key) ??
                rule.target_stage_key)
              : "",
          activity:
            rule.appointment_activity_type_id != null
              ? (activityNameById.get(rule.appointment_activity_type_id) ?? "")
              : "",
        },
      };
    case "alta_intencion_sin_actividad":
      return {
        key: "resources.alert_rules.description.alta_intencion_sin_actividad",
        params: { hours: rule.hours_threshold ?? 0 },
      };
    default:
      // The catalogue is closed by design (ADR-TASK-003): a 5th rule type
      // needs a migration plus its own case here, never falls through.
      throw new Error(`Unknown alert rule type: ${rule.rule_type}`);
  }
}

/**
 * Acceptance criterion (TASK-006): "current thresholds" -- a compact value
 * summary next to the description, distinct wording from it (the raw
 * configured value, not the "why it fires" sentence).
 */
export function resolveAlertRuleThresholdSummary(
  rule: DescribableAlertRule,
  stageLabelByKey: ReadonlyMap<string, string>,
  activityNameById: ReadonlyMap<Identifier, string>,
): AlertRuleTranslation {
  switch (rule.rule_type) {
    case "sin_seguimiento":
    case "alta_intencion_sin_actividad":
      return {
        key: "resources.alert_rules.threshold.hours",
        params: { smart_count: rule.hours_threshold ?? 0 },
      };
    case "detenido_en_etapa":
      return {
        key: "resources.alert_rules.threshold.stuck_count",
        params: { smart_count: rule.stuck_lead_count_threshold ?? 0 },
      };
    case "cumple_criterio_sin_cita":
      return {
        key: "resources.alert_rules.threshold.stage_activity",
        params: {
          stage:
            rule.target_stage_key != null
              ? (stageLabelByKey.get(rule.target_stage_key) ??
                rule.target_stage_key)
              : "",
          activity:
            rule.appointment_activity_type_id != null
              ? (activityNameById.get(rule.appointment_activity_type_id) ?? "")
              : "",
        },
      };
    default:
      throw new Error(`Unknown alert rule type: ${rule.rule_type}`);
  }
}

export interface StageWithTargetTime {
  key: string;
  label: string;
}

/**
 * Acceptance criterion (TASK-006): for "Detenido en etapa", the stages that
 * currently have a target time (read from EP) -- the rule applies only to
 * those. Ordered `(position, key)`, same tie-break as
 * `resolveStageActivitySummary` in `stageConfigForm.ts`.
 */
export function resolveStagesWithTargetTime(
  stageConfigs: readonly Pick<
    DealStageConfig,
    "key" | "label" | "position" | "target_hours" | "archived_at"
  >[],
): StageWithTargetTime[] {
  return stageConfigs
    .filter((stage) => stage.archived_at == null && stage.target_hours != null)
    .slice()
    .sort((a, b) => a.position - b.position || a.key.localeCompare(b.key))
    .map((stage) => ({ key: stage.key, label: stage.label }));
}

export interface AlertRuleChoice {
  id: string | number;
  name: string;
}

/**
 * "Etapa objetivo" picker (`target_stage_key`): only non-archived EP stages,
 * ordered `(position, key)`.
 */
export function buildStageChoices(
  stageConfigs: readonly Pick<
    DealStageConfig,
    "key" | "label" | "position" | "archived_at"
  >[],
): AlertRuleChoice[] {
  return stageConfigs
    .filter((stage) => stage.archived_at == null)
    .slice()
    .sort((a, b) => a.position - b.position || a.key.localeCompare(b.key))
    .map((stage) => ({ id: stage.key, name: stage.label }));
}

/**
 * "Actividad de cita" picker (`appointment_activity_type_id`): only
 * non-archived EA activity types, ordered `(position, id)`.
 */
export function buildActivityChoices(
  activityTypes: readonly Pick<
    DealActivityType,
    "id" | "name" | "position" | "archived_at"
  >[],
): AlertRuleChoice[] {
  return activityTypes
    .filter((activity) => activity.archived_at == null)
    .slice()
    .sort(
      (a, b) =>
        a.position - b.position ||
        String(a.id).localeCompare(String(b.id), undefined, { numeric: true }),
    )
    .map((activity) => ({ id: activity.id, name: activity.name }));
}
